import { Mail, Phone } from "lucide-react";
import { buildWhatsAppUrl, WhatsAppIcon } from "@/lib/whatsapp";
import { formatPhone } from "@/components/patients/PatientListRow";
import { Card, CardEmpty } from "./QuadroBase";
import type { PacienteDoQuadro, QuadroProps } from "./tipos";

const botao =
  "inline-flex h-9 items-center justify-center gap-1.5 rounded-md border px-3 text-sm font-semibold transition-colors";

/** Falar com o paciente de um toque: WhatsApp, ligação e e-mail, com o número à vista. */
export function QuadroContato({ menu, paciente }: Pick<QuadroProps, "menu"> & { paciente: PacienteDoQuadro }) {
  const celular = paciente.phone || null;
  const fixo = paciente.landline || null;
  // WhatsApp em branco = o próprio celular; quando é outro número, ganha linha própria
  const whatsappProprio = paciente.whatsapp && paciente.whatsapp !== celular ? paciente.whatsapp : null;
  const nada = !celular && !whatsappProprio && !fixo && !paciente.email;

  return (
    <Card icon={<Phone className="h-4 w-4" />} title="Contato rápido" tone="emerald" menu={menu}>
      {nada ? (
        <CardEmpty icon={<Phone className="h-6 w-6" />} text="Sem contato cadastrado" hint="Adicione celular ou e-mail na aba Dados" />
      ) : (
        <div className="space-y-3">
          {celular && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Celular</p>
              <p className="text-sm font-medium tabular-nums text-zinc-900 dark:text-foreground">{formatPhone(celular)}</p>
              <div className={`mt-2 grid gap-2 ${whatsappProprio ? "grid-cols-1" : "grid-cols-2"}`}>
                {!whatsappProprio && (
                  <a
                    href={buildWhatsAppUrl(celular, paciente.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${botao} border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500/30 dark:text-emerald-400 dark:hover:bg-emerald-500/10`}
                  >
                    <WhatsAppIcon className="h-4 w-4" />
                    WhatsApp
                  </a>
                )}
                <a href={`tel:${celular.replace(/\D/g, "")}`} className={`${botao} border-zinc-200 text-zinc-700 hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted`}>
                  <Phone className="h-4 w-4" />
                  Ligar
                </a>
              </div>
            </div>
          )}
          {whatsappProprio && (
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">WhatsApp</p>
                <p className="text-sm font-medium tabular-nums text-zinc-900 dark:text-foreground">{formatPhone(whatsappProprio)}</p>
              </div>
              <a href={buildWhatsAppUrl(whatsappProprio, paciente.name)} target="_blank" rel="noopener noreferrer" className={`${botao} border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500/30 dark:text-emerald-400 dark:hover:bg-emerald-500/10`}>
                <WhatsAppIcon className="h-4 w-4" />
                WhatsApp
              </a>
            </div>
          )}
          {fixo && (
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Fixo</p>
                <p className="text-sm font-medium tabular-nums text-zinc-900 dark:text-foreground">{formatPhone(fixo)}</p>
              </div>
              <a href={`tel:${fixo.replace(/\D/g, "")}`} className={`${botao} border-zinc-200 text-zinc-700 hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted`}>
                <Phone className="h-4 w-4" />
                Ligar
              </a>
            </div>
          )}
          {paciente.email && (
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">E-mail</p>
                <p className="truncate text-sm font-medium text-zinc-900 dark:text-foreground">{paciente.email}</p>
              </div>
              <a href={`mailto:${paciente.email}`} className={`${botao} shrink-0 border-zinc-200 text-zinc-700 hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted`}>
                <Mail className="h-4 w-4" />
                E-mail
              </a>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
